export interface NavEntry {
  key:
    | 'operations'
    | 'statistics'
    | 'tenants'
    | 'applications'
    | 'credentials'
    | 'gateway-accounts'
    | 'audit'
    | 'test-bench';
  path: string;
  labelKey: string;
}

export interface NavGroup {
  key: 'operation' | 'configuration';
  labelKey: string;
  entries: NavEntry[];
}

/**
 * Rail navigation (hybrid-3c.md v3.9 Pattern A / v2 §5.1: "grouped in
 * sections with group labels, not a flat list"). The entries and their
 * routes/keys are unchanged from the flat list the earlier waves wired —
 * only the visual grouping is new, added for this shell migration:
 * "Operación" clusters the day-to-day monitoring screens, "Configuración"
 * clusters the administration entities.
 *
 * `test-bench` is a run-6 addition: its menu item is only rendered by
 * `CustomMenu` when `useTestBenchAvailability` reports the feature enabled
 * (404/403 from the backend hides it) — this array always lists it, the
 * gating lives in the menu component, not here.
 */
export const navGroups: NavGroup[] = [
  {
    key: 'operation',
    labelKey: 'pay.nav.groups.operation',
    entries: [
      { key: 'operations', path: '/operations', labelKey: 'pay.nav.operations' },
      { key: 'statistics', path: '/statistics', labelKey: 'pay.nav.statistics' },
      { key: 'test-bench', path: '/test-bench', labelKey: 'pay.nav.testBench' },
      { key: 'audit', path: '/audit', labelKey: 'pay.nav.audit' },
    ],
  },
  {
    key: 'configuration',
    labelKey: 'pay.nav.groups.configuration',
    entries: [
      { key: 'tenants', path: '/tenants', labelKey: 'pay.nav.tenants' },
      { key: 'applications', path: '/applications', labelKey: 'pay.nav.applications' },
      { key: 'credentials', path: '/credentials', labelKey: 'pay.nav.credentials' },
      { key: 'gateway-accounts', path: '/gateway-accounts', labelKey: 'pay.nav.gatewayAccounts' },
    ],
  },
];

export const isActiveRoute = (pathname: string, to: string): boolean => {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
};

/** Finds the group + entry whose route matches the current path, longest `path` first
 *  so a detail route (e.g. `/operations/:id`) resolves to its own list entry. */
export const findNavEntryForPath = (
  pathname: string,
): { group: NavGroup; entry: NavEntry } | null => {
  const candidates = navGroups.flatMap((group) => group.entries.map((entry) => ({ group, entry })));
  candidates.sort((a, b) => b.entry.path.length - a.entry.path.length);
  return candidates.find(({ entry }) => isActiveRoute(pathname, entry.path)) ?? null;
};
